import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../../Translation";
import Badge from "../../Badge";
import { ArrowDownIcon, ArrowsPointingOutIcon, CalculatorIcon, ChevronDownIcon, Cog8ToothIcon, DocumentIcon, ExclamationCircleIcon, MinusIcon, PlusIcon, VariableIcon } from "@heroicons/react/24/outline";
import { split, webhook } from "../../../Resources/Images";
import { TextInput } from "../../Inputs/TextInput";
import { TextArea } from "../../Inputs/TextArea";
import { Button } from "../../Buttons";
import Tabs, { useTabs } from "../../Tabs";
import { useAPI } from "../../../API";
import Select from "../../Inputs/Select";
import { bgColor, hoverBgColor, textColor } from "../../../Resources/CSS/styling";


const ConfigurationTab = ({ tmpStep, setTmpStep, save, error, isCreate }) => {

    const { translate } = useTranslation();
    const api = useAPI();

    const isEventCount = tmpStep.data.type.toLowerCase() === 'event';


    const sourceOptions = [
        { id: 'event', name: translate('automatisation.from_event') },
        { id: 'contact', name: translate('automatisation.from_contact') },
        { id: 'global', name: translate('automatisation.from_global') }
    ];

    const selectedSource = sourceOptions.find(source => tmpStep.data.value?.startsWith && tmpStep.data.value?.startsWith(source.id)) || sourceOptions[0];

    const timeframeUnits = [
        { id: 2, name: translate('automatisation.minutes'), value: 'MINUTES', coefficient: 1 },
        { id: 3, name: translate('automatisation.hours'), value: 'HOURS', coefficient: 60 },
        { id: 4, name: translate('automatisation.days'), value: 'DAYS', coefficient: 1440 },
    ];

    const contactExternalId = tmpStep.data.contact_external_id;
    const setContactExternalId = (contactExternalId) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, contact_external_id: contactExternalId } });
    }

    const eventName = tmpStep.data.value;

    const setEventName = (eventName) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value: eventName } });
    }

    const timeframeUnit = timeframeUnits.find((unit) => unit.value.toLowerCase() === tmpStep.data.timeframe_unit?.toLowerCase()) || timeframeUnits[0];

    const setTimeframeUnit = (newTimeframeUnit) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, timeframe_unit: newTimeframeUnit.value, timeframe: tmpStep.data.timeframe / timeframeUnit.coefficient * newTimeframeUnit.coefficient } });
    }

    const timeframeValue = (tmpStep.data.timeframe || 1) / timeframeUnit.coefficient;

    const setTimeframeValue = (timeframeValue) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, timeframe: timeframeValue * timeframeUnit.coefficient } });
    }

    const variableTypes = [
        { id: 1, name: translate('automatisation.string'), value: 'string' },
        { id: 2, name: translate('automatisation.number'), value: 'number' },
        { id: 3, name: translate('automatisation.boolean'), value: 'boolean' },
        { id: 4, name: translate('automatisation.string_variable'), value: 'string_variable' },
        { id: 5, name: translate('automatisation.number_variable'), value: 'number_variable' },
        { id: 6, name: translate('automatisation.boolean_variable'), value: 'boolean_variable' }
    ]

    const getOperationOptions = (variableType) => {
        const operations = [
            { id: 1, name: translate('automatisation.is_equal'), value: 'EQUALS' },
            { id: 2, name: translate('automatisation.is_not_equal'), value: 'NOT_EQUALS' },
            ...(variableType === 'number' || variableType === 'number_variable' ? [{ id: 3, name: translate('automatisation.is_greater'), value: 'GREATER_THAN' },{ id: 4, name: translate('automatisation.is_less'), value: 'LESS_THAN' }] : []),
            ...(variableType === 'string' || variableType === 'string_variable' ? [{ id: 5, name: translate('automatisation.contains'), value: 'CONTAINS' }, { id: 6, name: translate('automatisation.does_not_contain'), value: 'NOT_CONTAINS' }] : []),
            ...(variableType === 'number_variable' || variableType === 'string_variable' || variableType === 'boolean_variable' ? [] : [{ id: 7, name: translate('automatisation.is_null_or_empty'), value: 'IS_NULL_OR_EMPTY' }, { id: 8, name: translate('automatisation.is_not_null_or_empty'), value: 'IS_NOT_NULL_OR_EMPTY' }]),
        ];
        return operations;
    };

    const variableSourceOptions = [
        { id: 'event', name: translate('automatisation.from_event') },
        { id: 'contact', name: translate('automatisation.from_contact') },
        { id: 'global', name: translate('automatisation.from_global') }
    ];

    const addContactFilter = () => {
        const newMapping = tmpStep.data.filters ? [...tmpStep.data.filters, { variable: '', variable_type: 'STRING', operation: 'EQUALS', value: '' }] : [{ variable: '', variable_type: 'STRING', operation: 'EQUALS', value: '' }];
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, filters: newMapping } });
    }
  
    const removeContactFilter = (index) => {
        const newMapping = tmpStep.data.filters.filter((_, i) => i !== index);
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, filters: newMapping } });
    }

    const updateContactFilter = (index, key, value) => {
        const newMapping = [...tmpStep.data.filters];
        newMapping[index][key] = value;
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, filters: newMapping } });
    }

    const getVariableType = (variableType) => {
        return variableTypes.find((type) => type.value.toLowerCase() === variableType.toLowerCase()) || variableTypes[0];
    }

    const getOperation = (operation, variableType) => {
        return getOperationOptions(getVariableType(variableType).value).find((op) => op.value.toLowerCase() === operation.toLowerCase()) || getOperationOptions(getVariableType(variableType))[0];
    }

    const getVariableSource = (variableSourceOptions, variableValue) => {
        return variableSourceOptions.find(source => variableValue?.startsWith && variableValue?.startsWith(source.id)) || variableSourceOptions[0];
    }

    const setValueSource = (source, index) => {
        const contactFilter = tmpStep.data.filters[index];
        const newVariableValueWithoutSource = contactFilter.value.split(".").slice(1).join(".");
        updateContactFilter(index, 'value', source.id + newVariableValueWithoutSource);
    }

    const updateValueWithVariableSource = (index, key, value) => {
        const contactFilter = tmpStep.data.filters[index];
        const source = getVariableSource(variableSourceOptions, contactFilter.value);
        updateContactFilter(index, key, source.id + "." + value);
    }

    const setValue = (value) => {
        const newValueWithoutSource = selectedSource.id + "." + value;
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value: newValueWithoutSource } });
    }

    const setVariableSource = (source) => {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value: source.id + tmpStep.data.value.split(selectedSource.id)[1] } });
    }

    const variableValue = tmpStep.data.value.split(".").slice(1).join(".");
  
    return (
        <div className="flex flex-col mt-4 overflow-visible">

            {error && <p className="text-red-600 flex flex-row items-center bg-red-50 p-4"><ExclamationCircleIcon className="w-6 h-6 mr-2" />{error}</p>}
            <p className="text-md text-gray-600 font-bold">{translate('automatisation.count_result_explication')}</p>
            

            {isEventCount && <div>
                <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.external_id')}</p>
                <TextInput value={contactExternalId} onChange={(e) => setContactExternalId(e.target.value)} />

                <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.event_name')}</p>
                <TextInput value={eventName} onChange={(e) => setEventName(e.target.value)} />

                <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.timeframe')}</p>

                <p className="text-md text-gray-600 font-medium mt-2">{translate('automatisation.timeframe_value')}</p>
                <TextInput value={timeframeValue} onChange={(e) => setTimeframeValue(e.target.value)} />

                <p className="text-md text-gray-600 font-medium mt-2">{translate('automatisation.timeframe_unit')}</p>
                <Select selected={timeframeUnit} options={timeframeUnits} setSelected={(e) => setTimeframeUnit(e)} />

                <div className="flex flex-col mt-4">
                <div className="flex flex-row items-center justify-between">
                    <p className="text-xl text-gray-600 font-bold">{translate('automatisation.filters')}</p>
                    <PlusIcon onClick={addContactFilter} className="w-6 h-6 text-gray-600 cursor-pointer hover:text-gray-300" />
                </div>

                <div className="flex flex-col w-full flex-1 gap-4 mt-2">
                    {(tmpStep.data.filters || []).map((filter, index) => (
                        <div className="flex flex-col justify-between border border-gray-400 rounded-md p-2">
                            <div className="flex flex-row items-center justify-between">
                                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.variable')}</p>
                                <MinusIcon onClick={() => removeContactFilter(index)} className="w-6 h-6 text-red-600 cursor-pointer ml-2 hover:text-red-300" />
                            </div>
                            <div className="flex flex-1 w-full flex-col">
                                <TextInput value={filter.variable} onChange={(e) => updateContactFilter(index, 'variable', e.target.value)} />
                            </div>
                            <div className="flex flex-1 w-full flex-col mt-2">
                                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.operation')}</p>
                                <Select options={getOperationOptions(getVariableType(filter.variable_type).value)} selected={getOperation(filter.operation, filter.variable_type)} setSelected={(e) => updateContactFilter(index, 'operation', e.value)} />
                            </div>
                            <div className="flex flex-1 w-full flex-col mt-2">
                                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.value_type')}</p>
                                <Select options={variableTypes} selected={getVariableType(filter.variable_type)} setSelected={(e) => updateContactFilter(index, 'variable_type', e.value)} />
                            </div>

                            {(getVariableType(filter.variable_type).value === 'string_variable' || getVariableType(filter.variable_type).value === 'number_variable' || getVariableType(filter.variable_type).value === 'boolean_variable') && <div className="flex flex-1 w-full flex-col mt-2">
                                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.from')}</p>
                                <Select options={variableSourceOptions} selected={getVariableSource(variableSourceOptions, filter.value)} setSelected={(e) => setValueSource(e, index)} />
                                
                                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.value')}</p>
                                <TextInput value={filter.value.split(".").slice(1).join(".")} onChange={(e) => updateValueWithVariableSource(index, 'value', e.target.value)} />
                            </div>}
                            
                            {!getVariableType(filter.variable_type).value?.includes('variable') && !(getOperation(filter.operation, filter.variable_type).value == 'IS_NULL_OR_EMPTY' || getOperation(filter.operation, filter.variable_type).value == 'IS_NOT_NULL_OR_EMPTY') && <div className="flex flex-1 w-full flex-col">
                                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.value')}</p>
                                <TextInput value={filter.value} onChange={(e) => updateContactFilter(index, 'value', e.target.value)} />
                            </div>}
                        </div>
                    ))}
                    </div>
                </div>
            </div>}

            {!isEventCount && <div>
                <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.variable')}</p>
                <Select options={variableSourceOptions} selected={getVariableSource(variableSourceOptions, tmpStep.data.value)} setSelected={(e) => setVariableSource(e)} />

                <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.value')}</p>
                <TextInput value={variableValue} onChange={(e) => setValue(e.target.value)} />
            </div>}
            

          
            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep)}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

const DescriptionTab = ({ tmpStep, setTmpStep, save, isCreate }) => {
    const { translate } = useTranslation();
    return (
        <div className="flex flex-col mt-4">
            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.description')}</p>
            <TextArea rows={5} value={tmpStep.description} onChange={(e) => setTmpStep({ ...tmpStep, description: e.target.value })} />

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep, JSON.stringify(tmpStep.data.body))}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

export default function CountPanel({ setStepToClone, panelHook, step, refresh, deleteStep, isCreate, onCreateNewStep }) {
    const { translate } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [tmpStep, setTmpStep] = useState({ ...step });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const api = useAPI();

    const tabs = useTabs([
        { icon: Cog8ToothIcon, name: translate('automatisation.configuration'), current: true },
        { icon: DocumentIcon, name: translate('automatisation.description'), current: false }
    ]);
    
    // Reset tmpStep when panel is closed
    useEffect(() => {
        if (!panelHook.open) {
            setTmpStep({ ...step });
        }
    }, [panelHook.open]);

    const save = async (tmpStep) => {


        try {
            if (isCreate) {
                if (!tmpStep.description || tmpStep.description.trim() === '') {
                   if (tmpStep.data.type.toLowerCase() === 'event') {
                    tmpStep.description = "Count events for " + tmpStep.data.value;
                   } else {
                    tmpStep.description = "Count items inside " + tmpStep.data.value;
                   }
                }

                const newStepResponse = await api.createAutomatisationStep(tmpStep.automatisation_id, tmpStep);
                if (newStepResponse.data.automatisation_step) {
                    onCreateNewStep(newStepResponse.data.automatisation_step);
                }
            } else {
                await api.updateAutomatisationStep(tmpStep.automatisation_id, tmpStep);
            }

            panelHook.setOpen(false);
            setTimeout(() => {
                refresh();
            }, 1000);
        } catch (e) {
            setError('Invalid JSON');
            return;
        }

    }

    const selectStepToClone = (step) => {
        setStepToClone(step);
        panelHook.setOpen(false);
    }

    const isEventCount = tmpStep.data.type.toLowerCase() === 'event';


    return (
        <div className="flex flex-col flex-1 rounded-md p-4 overflow-hidden h-full w-96">
            <div className="flex flex-row items-center">
                <CalculatorIcon className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md mr-2 ${textColor}`} />

                <div className="flex flex-col ml-2">
                    <h2 className="text-xl font-bold">{translate('automatisation.count')}</h2>
                    <h3 className="text-gray-600 font-medium text-wrap break-all">{translate(isEventCount ? 'automatisation.event_count' : 'automatisation.items_count')}</h3>
                </div>
                
                <div className="flex flex-row flex-1 justify-end">

                {!isCreate && <Button className="bg-red-600 hover:bg-red-300 ml-4" onClick={deleteStep}>
                        {translate('delete')}
                    </Button>}
                {isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => save(tmpStep)}>{translate('create')}</Button>}
                {!isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => selectStepToClone(tmpStep)}>{translate('clone')}</Button>}
                </div>
            </div>

            <div className="flex flex-row overflow-x-scroll no-scrollbar">
                <Tabs tabs={tabs} />
            </div>

            {tabs.currentTabIndex === 0 && <ConfigurationTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} />}
            {tabs.currentTabIndex === 1 && <DescriptionTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} />}
        </div>
    )
}